/* iter-50j — POD Queue admin panel.
 *
 * Lists paid Print-On-Demand orders. Each row exposes the address, the
 * signed-vs-unsigned flag, the current `queue_status`, and a transition
 * button driven by the backend's whitelist (awaiting_signature →
 * pending_print → printing → shipped → delivered). Tracking number can
 * be entered when transitioning to "shipped".
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Truck, PenLine, Send, PackageCheck, Ban } from 'lucide-react';
import { bookstoreAdminApi } from '@/lib/bookstoreApi';

const STATUSES = [
  { v: '',                   label: 'All' },
  { v: 'awaiting_signature', label: 'Awaiting signature' },
  { v: 'pending_print',      label: 'Pending print' },
  { v: 'printing',           label: 'Printing' },
  { v: 'shipped',            label: 'Shipped' },
  { v: 'delivered',          label: 'Delivered' },
  { v: 'cancelled',          label: 'Cancelled' },
];

const NEXT_ACTION = {
  awaiting_signature: { to: 'pending_print', label: 'Mark signed', icon: PenLine },
  pending_print:      { to: 'printing',      label: 'Start printing', icon: PackageCheck },
  printing:           { to: 'shipped',       label: 'Mark shipped', icon: Truck },
  shipped:            { to: 'delivered',     label: 'Mark delivered', icon: Send },
};

export default function PodQueuePanel() {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState({});
  const [tracking, setTracking] = useState({});

  const load = useCallback(async () => {
    setRows(null);
    try {
      const d = await bookstoreAdminApi.listPodOrders(status || undefined);
      setRows(d.orders || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load POD queue');
      setRows([]);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const advance = async (row, to) => {
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      const body = { queue_status: to };
      if (to === 'shipped' && tracking[row.id]) body.tracking = tracking[row.id];
      await bookstoreAdminApi.updatePodStatus(row.id, body);
      toast.success(`#${row.id.slice(0, 10)} → ${to}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update status');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[row.id]; return n; });
    }
  };

  const cancel = async (row) => {
    if (!window.confirm(`Cancel POD order ${row.id}? This is final.`)) return;
    setBusy((b) => ({ ...b, [row.id]: true }));
    try {
      await bookstoreAdminApi.updatePodStatus(row.id, { queue_status: 'cancelled' });
      toast.success('Cancelled');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not cancel');
    } finally {
      setBusy((b) => { const n = { ...b }; delete n[row.id]; return n; });
    }
  };

  const totals = useMemo(() => {
    const t = { signed: 0, unsigned: 0, awaiting: 0, ready: 0, shipped: 0 };
    (rows || []).forEach((r) => {
      if (r.signed) t.signed += 1; else t.unsigned += 1;
      if (r.queue_status === 'awaiting_signature') t.awaiting += 1;
      if (r.queue_status === 'pending_print')      t.ready += 1;
      if (r.queue_status === 'shipped')            t.shipped += 1;
    });
    return t;
  }, [rows]);

  return (
    <div data-testid="bs-admin-pod" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight">Print-On-Demand queue</h2>
          <p className="mt-0.5 text-[11.5px] text-muted-foreground">
            {rows ? `${rows.length} order${rows.length === 1 ? '' : 's'}` : 'Loading…'} ·
            {' '}{totals.signed} signed · {totals.unsigned} unsigned · {totals.awaiting} awaiting sig · {totals.ready} ready · {totals.shipped} shipped
          </p>
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="bs-admin-pod-filter"
          className="h-8 rounded-sm border border-border bg-background px-2 text-[12px] outline-none focus:border-primary"
        >
          {STATUSES.map((s) => <option key={s.v || 'all'} value={s.v}>{s.label}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-card">
        {rows === null ? (
          <div className="flex items-center gap-2 px-4 py-6 text-[12px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading queue…
          </div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-6 text-[12px] text-muted-foreground">No POD orders match this filter.</div>
        ) : (
          <table className="w-full text-[12px]">
            <thead className="bg-panel-header/40 text-[10.5px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Order</th>
                <th className="px-3 py-2 text-left">Book</th>
                <th className="px-3 py-2 text-left">Ship to</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const next = NEXT_ACTION[r.queue_status];
                const Icon = next?.icon;
                return (
                  <tr key={r.id} className="border-t border-border align-top" data-testid={`bs-admin-pod-row-${r.id}`}>
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">{r.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="px-3 py-2">
                      <div className="font-mono-stat text-[11px]">{r.id}</div>
                      <div className="text-[10.5px] text-muted-foreground">
                        ${(r.amount_cents / 100).toFixed(2)} · {r.signed ? 'signed' : 'unsigned'}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{r.book_title}</div>
                      <div className="text-[10.5px] text-muted-foreground">{r.book_author}</div>
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      <div className="font-semibold">{r.address?.name}</div>
                      <div className="text-muted-foreground">{r.address?.line1}{r.address?.line2 ? `, ${r.address.line2}` : ''}</div>
                      <div className="text-muted-foreground">
                        {r.address?.city}, {r.address?.region} {r.address?.postal} · {r.address?.country}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <StatusPill status={r.queue_status} />
                      {r.signature_email_status && r.queue_status === 'awaiting_signature' && (
                        <div className="mt-1 text-[10px] text-muted-foreground">email: {r.signature_email_status}</div>
                      )}
                      {r.tracking && (
                        <div className="mt-1 text-[10px] text-muted-foreground">tracking: {r.tracking}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex flex-col items-end gap-1">
                        {next && r.queue_status === 'printing' && (
                          <input
                            type="text"
                            placeholder="Tracking #"
                            value={tracking[r.id] || ''}
                            onChange={(e) => setTracking((t) => ({ ...t, [r.id]: e.target.value }))}
                            data-testid={`bs-admin-pod-tracking-${r.id}`}
                            className="h-7 w-32 rounded-sm border border-border bg-background px-1 text-[11px]"
                          />
                        )}
                        {next && (
                          <button
                            type="button"
                            disabled={!!busy[r.id]}
                            onClick={() => advance(r, next.to)}
                            data-testid={`bs-admin-pod-advance-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-sm bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
                          >
                            {Icon ? <Icon className="h-3 w-3" /> : null}
                            {busy[r.id] ? '…' : next.label}
                          </button>
                        )}
                        {r.queue_status !== 'delivered' && r.queue_status !== 'cancelled' && (
                          <button
                            type="button"
                            disabled={!!busy[r.id]}
                            onClick={() => cancel(r)}
                            data-testid={`bs-admin-pod-cancel-${r.id}`}
                            className="inline-flex items-center gap-1 rounded-sm border border-rose-300/50 bg-rose-50 px-2 py-1 text-[10.5px] text-rose-800 hover:border-rose-400 disabled:opacity-60"
                          >
                            <Ban className="h-3 w-3" /> Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const STATUS_TONE = {
  awaiting_signature: 'bg-amber-50 text-amber-800',
  pending_print:      'bg-secondary text-muted-foreground',
  printing:           'bg-indigo-50 text-indigo-800',
  shipped:            'bg-sky-50 text-sky-800',
  delivered:          'bg-emerald-50 text-emerald-800',
  cancelled:          'bg-rose-50 text-rose-800',
};

function StatusPill({ status }) {
  const tone = STATUS_TONE[status] || 'bg-secondary text-muted-foreground';
  return (
    <span className={`inline-flex rounded-sm px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider ${tone}`}>
      {status}
    </span>
  );
}
